import { useEffect, useState } from 'react';
import axios from 'axios';
import { Ban, Loader2, RefreshCw, TrendingDown, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/pick-rules`;
const STATS = {
  nba: [['', 'All stats'], ['points', 'Points'], ['rebounds', 'Rebounds'], ['assists', 'Assists'], ['threes', '3-pointers'], ['steals', 'Steals'], ['blocks', 'Blocks']],
  nfl: [['', 'All stats'], ['passing_yards', 'Passing yards'], ['passing_attempts', 'Pass attempts'], ['passing_completions', 'Completions'], ['passing_tds', 'Passing TDs'],
    ['rushing_yards', 'Rushing yards'], ['receiving_yards', 'Receiving yards'], ['receptions', 'Receptions'], ['anytime_td', 'Anytime TD']],
};
const statName = (lg, s) => (STATS[lg] || []).find(([k]) => k === s)?.[1] || (s ? s.replace(/_/g, ' ') : 'All stats');

const AddBlock = ({ onSaved }) => {
  const [league, setLeague] = useState('nba');
  const [player, setPlayer] = useState('');
  const [stat, setStat] = useState('points');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!player.trim()) return;
    setBusy(true);
    try {
      onSaved((await axios.post(`${API}/blocks`, { league, player: player.trim(), stat: stat || null })).data);
      toast.success(`${player.trim()} won't be picked for ${statName(league, stat).toLowerCase()}`);
      setPlayer('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="pick-block-form">
      <select value={league} onChange={(e) => { setLeague(e.target.value); setStat(''); }} className="rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm" data-testid="pick-block-league">
        <option value="nba">NBA</option><option value="nfl">NFL</option>
      </select>
      <input value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="Player name (e.g. Domantas Sabonis)" onKeyDown={(e) => e.key === 'Enter' && save()}
        className="min-w-[180px] flex-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-white placeholder:text-zinc-500" data-testid="pick-block-player" />
      <select value={stat} onChange={(e) => setStat(e.target.value)} className="rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm" data-testid="pick-block-stat">
        {STATS[league].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <button type="button" onClick={save} disabled={busy || !player.trim()} className="rounded-md bg-red-500 px-3 py-1.5 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-50" data-testid="pick-block-add">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
      </button>
    </div>
  );
};

// "Do not pick" list + lines lowered automatically from your missed ticket legs.
export default function PickRulesPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(API).then((r) => setData(r.data)).catch(() => setData({ blocks: [], lowered: [] })); }, []);
  const remove = async (id) => setData((await axios.delete(`${API}/blocks/${id}`)).data);
  const relearn = async () => {
    setBusy(true);
    try { setData((await axios.post(`${API}/learn`)).data); toast.success('Picks updated from your tickets'); } catch { toast.error('Could not update'); }
    setBusy(false);
  };
  if (!data) return null;
  return (
    <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3" data-testid="pick-rules-panel">
      <div>
        <p className="flex items-center gap-1.5 text-sm font-bold text-red-300"><Ban className="h-4 w-4" />Do not pick</p>
        <p className="text-[11px] text-zinc-500">These players never show up in suggestions or ready-made tickets for that stat.</p>
      </div>
      <AddBlock onSaved={setData} />
      <div className="flex flex-wrap gap-1.5" data-testid="pick-block-list">
        {data.blocks.length === 0 && <p className="text-xs text-zinc-500">No one blocked yet.</p>}
        {data.blocks.map((b) => (
          <span key={b.id} className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-xs text-red-100" data-testid={`pick-block-chip-${b.id}`}>
            {b.league.toUpperCase()} · {b.player} · {statName(b.league, b.stat)}
            <button type="button" onClick={() => remove(b.id)} aria-label="Remove" data-testid={`pick-block-remove-${b.id}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-zinc-800 pt-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-bold text-amber-300"><TrendingDown className="h-4 w-4" />Lines lowered from your tickets</p>
          <p className="text-[11px] text-zinc-500">Missed a stat 2+ times on recent tickets → suggestions use a lower line he actually reaches.</p>
        </div>
        <button type="button" onClick={relearn} disabled={busy} className="inline-flex shrink-0 items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-xs font-semibold hover:bg-zinc-800" data-testid="pick-rules-relearn">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Re-check tickets
        </button>
      </div>
      <ul className="space-y-1" data-testid="pick-lowered-list">
        {data.lowered.length === 0 && <li className="text-xs text-zinc-500">Nothing lowered yet.</li>}
        {data.lowered.map((c) => (
          <li key={c.key} className="text-xs text-zinc-300" data-testid={`pick-lowered-${c.key}`}>
            <b className="text-white">{c.name}</b> · {statName(c.league, c.stat)}: missed {c.misses} of {c.legs}
            {c.actuals?.length ? ` (put up ${c.actuals.join(', ')})` : ''} → now <b className="text-amber-300">{c.cap} or under</b>
          </li>
        ))}
      </ul>
    </div>
  );
}
